import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { invitationStore, inviteTokenSelector } from '@stores/invitation.store';
import { useLocalSessionStore } from '@stores/local-session.store';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog as usePosthog } from 'posthog-js/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'zustand';
import { routes } from '../routes';
import { UserProfileRole, type UserSelectDto } from '../types/api.types';
import { removeSelectedCompanyId, setSelectedCompanyId } from '../utils/company.util';
import { queryClient } from './client';
import { queryKeys } from './keys';

export const useCurrentUserQueryOptions = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const inviteToken = useStore(invitationStore, inviteTokenSelector);

  const enabled = isAuthenticated;

  return queryOptions<UserSelectDto, ApiErrorWrapper>({
    enabled,
    queryKey: enabled ? queryKeys.users.getCurrentUser : queryKeys.placeholders.default,
    queryFn: async () => {
      return apiGet<UserSelectDto>({ query: { inviteToken }, path: '/v1/users/me' });
    },
    refetchOnWindowFocus: true,
  });
};

const useUserSessionEffects = (user?: UserSelectDto) => {
  const posthog = usePosthog();
  const { hasLocalSession, setHasLocalSession } = useLocalSessionStore();

  useEffect(() => {
    if (!user) return;

    posthog.identify(user.id, { email: user.email });

    if (!hasLocalSession) {
      setHasLocalSession(true);
    }

    const isCompanyScopedRole =
      user.profile?.role === UserProfileRole.INNOVATOR || user.profile?.role === UserProfileRole.INTELLIGENCE;
    const hasValidCompany = Boolean(
      user.selectedCompanyId && user.companies?.some((company) => company.id === user.selectedCompanyId),
    );

    if (isCompanyScopedRole && hasValidCompany && user.selectedCompanyId) {
      setSelectedCompanyId(user.selectedCompanyId);
    } else {
      removeSelectedCompanyId();
    }
  }, [posthog, user, setHasLocalSession, hasLocalSession]);
};

export type UserRoleEndpoint = 'admin' | 'intel' | 'expert' | 'innovator';

export const useGetCurrentUser = () => {
  const currentUserQueryOptions = useCurrentUserQueryOptions();
  const response = useQuery(currentUserQueryOptions);

  const { data: user } = response;

  const isAdmin = user?.profile?.role === UserProfileRole.ADMIN;
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;
  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;

  const isCompanyAdmin = user?.selectedCompanyRole === 'Admin';
  const selectedCompany = user?.companies?.find((company) => company.id === user?.selectedCompanyId);
  const isSelectedCompanyParentOrganization = Boolean(selectedCompany?.isParentOrganization);

  useUserSessionEffects(user);

  let roleEndpoint: UserRoleEndpoint | null = null;

  if (isAdmin) roleEndpoint = 'admin';
  if (isIntel) roleEndpoint = 'intel';
  if (isExpert) roleEndpoint = 'expert';
  if (isInnovator) roleEndpoint = 'innovator';

  return {
    ...response,
    isAdmin,
    isCompanyAdmin,
    isExpert,
    isInnovator,
    isIntel,
    isSelectedCompanyParentOrganization,
    roleEndpoint,
    selectedCompany,
  };
};

export const useResetMyData = () => {
  const { apiDelete } = useApi();
  const { isAuthenticated } = useAuth0();

  const onError = (error: Error) => {
    console.log('Error resetting data:', error);
  };

  return useMutation<void, ApiErrorWrapper, void>({
    mutationFn: async () => {
      if (!isAuthenticated) throw new Error('User is not authenticated');
      await apiDelete({ path: '/v1/users/me' });
    },
    onError,
  });
};

export const useResendVerificationEmail = () => {
  const { apiPost } = useApi();
  const { isAuthenticated } = useAuth0();

  return useMutation<void, ApiErrorWrapper, void>({
    mutationFn: async () => {
      if (!isAuthenticated) throw new Error('User is not authenticated');
      await apiPost({ path: '/v1/users/me/resend-verification-email' });
    },
  });
};

export const useUpdateSelectedCompany = () => {
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  return useMutation<{ companyId: string }, void, { companyId: string }>({
    mutationFn: async ({ companyId }) => {
      if (!isAuthenticated) throw new Error('User is not authenticated');

      setSelectedCompanyId(companyId);
      queryClient.clear();
      navigate(routes.home.path);

      return { companyId };
    },
  });
};

export const useGetUserSelectedCompanyId = () => {
  const { data: user } = useGetCurrentUser();
  return user?.selectedCompanyId;
};

export const useStopImpersonation = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, void>({
    mutationFn: async () => {
      await apiPost({
        path: '/v1/users/me/stop-impersonation',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.getCurrentUser });
      window.location.reload();
    },
  });
};

export const useRefreshImpersonation = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, ApiErrorWrapper, void>({
    mutationFn: async () => {
      await apiPost({
        path: '/v1/users/me/refresh-impersonation',
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeys.users.getCurrentUser });
    },
  });
};

type RoleEndpointPrefix = 'expert' | 'innovator' | 'intel' | 'admin';

/**
 * Returns the role-based endpoint prefix for API calls
 */
export const useRoleEndpointPrefix = (): RoleEndpointPrefix | null => {
  const { data: user } = useGetCurrentUser();

  if (user?.profile?.role === UserProfileRole.INNOVATOR) return 'innovator';
  if (user?.profile?.role === UserProfileRole.EXPERT) return 'expert';
  if (user?.profile?.role === UserProfileRole.INTELLIGENCE) return 'intel';
  if (user?.profile?.role === UserProfileRole.ADMIN) return 'admin';

  return null;
};
